import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import ScrollToTop from "@/components/ScrollToTop";
import GlobalAlerts from "@/components/GlobalAlerts";
import OnboardingTour from "@/components/onboarding/OnboardingTour";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import FloatingCart from "@/components/FloatingCart";
import { preloadAllRoutes } from "@/lib/preloadRoutes";
import RequireRole from "@/components/auth/RequireRole";
import RequireInstructor from "@/components/auth/RequireInstructor";

// Keep Index eager — it's the LCP/home page
import Index from "./pages/Index";

// Transparent, non-layout-shifting fallback. Rendering a full-screen spinner
// here caused every lazy-route navigation to flash a blank "refresh" panel.
// Returning null keeps the chunk-load window invisible; warm chunks (see
// preloadAllRoutes) make navigation feel instant.
const RouteFallback = () => null;

// Lazy-load all other routes to reduce initial JS bundle
const Auth = lazy(() => import("./pages/Auth"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SignupChoice = lazy(() => import("./pages/SignupChoice"));
const Activities = lazy(() => import("./pages/Activities"));
const ActivityDetail = lazy(() => import("./pages/ActivityDetail"));
const Categories = lazy(() => import("./pages/Categories"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Packages = lazy(() => import("./pages/Packages"));
const PackageDetail = lazy(() => import("./pages/PackageDetail"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardBookings = lazy(() => import("./pages/dashboard/DashboardBookings"));
const DashboardAddresses = lazy(() => import("./pages/dashboard/DashboardAddresses"));
const DashboardPayments = lazy(() => import("./pages/dashboard/DashboardPayments"));
const DashboardInstallments = lazy(() => import("./pages/dashboard/DashboardInstallments"));
const DashboardGatherings = lazy(() => import("./pages/dashboard/DashboardGatherings"));
const DashboardSubscriptions = lazy(() => import("./pages/dashboard/DashboardSubscriptions"));
const Gatherings = lazy(() => import("./pages/Gatherings"));
const GatheringsManagement = lazy(() => import("./pages/admin/GatheringsManagement"));
const DashboardMyVote = lazy(() => import("./pages/dashboard/DashboardMyVote"));
const DashboardProfile = lazy(() => import("./pages/dashboard/DashboardProfile"));
const DashboardSettings = lazy(() => import("./pages/dashboard/DashboardSettings"));
const DashboardNotifications = lazy(() => import("./pages/dashboard/DashboardNotifications"));
const InstructorDashboard = lazy(() => import("./pages/InstructorDashboard"));
const InstructorBookings = lazy(() => import("./pages/instructor/InstructorBookings"));

const InstructorRevenue = lazy(() => import("./pages/instructor/InstructorRevenue"));
const InstructorProfile = lazy(() => import("./pages/instructor/InstructorProfile"));
const InstructorAvailability = lazy(() => import("./pages/instructor/InstructorAvailability"));
const EditActivity = lazy(() => import("./pages/instructor/EditActivity"));
const InstructorMessages = lazy(() => import("./pages/instructor/InstructorMessages"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const B2BCompaniesManagement = lazy(() => import("./pages/admin/B2BCompaniesManagement"));
const CategoryManagement = lazy(() => import("./pages/admin/CategoryManagement"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const HomePageManagement = lazy(() => import("./pages/admin/HomePageManagement"));
const SchoolManagement = lazy(() => import("./pages/admin/SchoolManagement"));
const AccountingManagement = lazy(() => import("./pages/admin/AccountingManagement"));
const CouponManagement = lazy(() => import("./pages/admin/CouponManagement"));
const CompaniesManagement = lazy(() => import("./pages/admin/CompaniesManagement"));
const CEODashboard = lazy(() => import("./pages/exec/CEODashboard"));
const CEOOkrs = lazy(() => import("./pages/exec/CEOOkrs"));
const CEOForecast = lazy(() => import("./pages/exec/CEOForecast"));
const CEOHealth = lazy(() => import("./pages/exec/CEOHealth"));
const CFODashboard = lazy(() => import("./pages/exec/CFODashboard"));
const CFOAccounting = lazy(() => import("./pages/exec/CFOAccounting"));
const CFOPayouts = lazy(() => import("./pages/exec/CFOPayouts"));
const CFORefunds = lazy(() => import("./pages/exec/CFORefunds"));
const CFOFraud = lazy(() => import("./pages/exec/CFOFraud"));
const CFOVendorEarnings = lazy(() => import("./pages/exec/CFOVendorEarnings"));
const CFOCashflow = lazy(() => import("./pages/exec/CFOCashflow"));
const CFORevenueBreakdown = lazy(() => import("./pages/exec/CFORevenueBreakdown"));
const CFOPipeline = lazy(() => import("./pages/exec/CFOPipeline"));
const CFOCustomerValue = lazy(() => import("./pages/exec/CFOCustomerValue"));
const CFOMonthlyBookings = lazy(() => import("./pages/exec/CFOMonthlyBookings"));
const CFOSettlements = lazy(() => import("./pages/exec/CFOSettlements"));

const CFOCosts = lazy(() => import("./pages/exec/CFOCosts"));
const CFOBudget = lazy(() => import("./pages/exec/CFOBudget"));

const COODashboard = lazy(() => import("./pages/exec/COODashboard"));
const COOVendors = lazy(() => import("./pages/exec/COOVendors"));
const COOSla = lazy(() => import("./pages/exec/COOSla"));
const COOSupport = lazy(() => import("./pages/exec/COOSupport"));
const Terms = lazy(() => import("./pages/Terms"));
const CancellationPolicy = lazy(() => import("./pages/CancellationPolicy"));
const Privacy = lazy(() => import("./pages/Privacy"));
const InstructorTerms = lazy(() => import("./pages/InstructorTerms"));
const PartnerDocs = lazy(() => import("./pages/PartnerDocs"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SchoolsDirectory = lazy(() => import("./pages/schools/SchoolsDirectory"));
const SchoolDetail = lazy(() => import("./pages/schools/SchoolDetail"));
const SchoolVote = lazy(() => import("./pages/schools/SchoolVote"));
const SchoolAdminDashboard = lazy(() => import("./pages/schools/SchoolAdminDashboard"));
const CreateSchool = lazy(() => import("./pages/schools/CreateSchool"));
const DepartmentHeadPortal = lazy(() => import("./pages/schools/DepartmentHeadPortal"));
const DepartmentSelection = lazy(() => import("./pages/schools/DepartmentSelection"));
const SchoolPollWinner = lazy(() => import("./pages/schools/SchoolPollWinner"));
const CreateUserPoll = lazy(() => import("./pages/polls/CreateUserPoll"));
const UserPollVote = lazy(() => import("./pages/polls/UserPollVote"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const RoleApplications = lazy(() => import("./pages/admin/RoleApplications"));
const Basket = lazy(() => import("./pages/Basket"));
const Checkout = lazy(() => import("./pages/Checkout"));
const AISearch = lazy(() => import("./pages/AISearch"));
const DashboardPasses = lazy(() => import("./pages/dashboard/DashboardPasses"));
const DashboardPassDetail = lazy(() => import("./pages/dashboard/DashboardPassDetail"));
const WalletPassesManagement = lazy(() => import("./pages/admin/WalletPassesManagement"));
const WalletScanner = lazy(() => import("./pages/admin/WalletScanner"));
const AdminSecurity = lazy(() => import("./pages/admin/AdminSecurity"));
const FlashDealsManagement = lazy(() => import("./pages/admin/FlashDealsManagement"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const BackupRestore = lazy(() => import("./pages/admin/BackupRestore"));
const DashboardExperiences = lazy(() => import("./pages/dashboard/DashboardExperiences"));
const Hosts = lazy(() => import("./pages/Hosts"));
const HostDetail = lazy(() => import("./pages/HostDetail"));
const CategoryLanding = lazy(() => import("./pages/CategoryLanding"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));
const AbTestingManagement = lazy(() => import("./pages/admin/AbTestingManagement"));
const WebhooksManagement = lazy(() => import("./pages/admin/WebhooksManagement"));
const PackagesManagement = lazy(() => import("./pages/admin/PackagesManagement"));
const UpaymentsLog = lazy(() => import("./pages/admin/UpaymentsLog"));
const SecuritySummary = lazy(() => import("./pages/admin/SecuritySummary"));
const SeoConsole = lazy(() => import("./pages/admin/SeoConsole"));
const SiteSettings = lazy(() => import("./pages/admin/SiteSettings"));
const SubscriptionDiscounts = lazy(() => import("./pages/admin/SubscriptionDiscounts"));
const SharedBankAccounts = lazy(() => import("./pages/admin/SharedBankAccounts"));

const B2BPortal = lazy(() => import("./pages/b2b/B2BPortal"));
const InstructorCalendar = lazy(() => import("./pages/instructor/InstructorCalendar"));
const TrackInstructor = lazy(() => import("./pages/booking/TrackInstructor"));


// Cache query results between navigations so revisiting a page does not show
// a fresh skeleton flash. This is the main reason pages "looked like they
// refreshed twice" — every navigation re-mounted and refetched immediately.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 minute — list/detail data is reused across nav
      gcTime: 5 * 60_000, // keep cache 5 min so Back button is instant
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const App = () => {
  useEffect(() => {
    preloadAllRoutes();
  }, []);
  return (
  <QueryClientProvider client={queryClient}>
    <LocaleProvider>
      <AuthProvider>
        <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <GlobalAlerts />
            <OnboardingTour />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/signup" element={<SignupChoice />} />
                <Route path="/activities" element={<Activities />} />
                <Route path="/activities/:id" element={<ActivityDetail />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/c/:slug" element={<CategoryLanding />} />
                
                <Route path="/favorites" element={<Favorites />} />
                <Route path="/basket" element={<Basket />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/ai-search" element={<AISearch />} />
                <Route path="/hosts" element={<Hosts />} />
                <Route path="/hosts/:id" element={<HostDetail />} />
                <Route path="/packages" element={<Packages />} />
                <Route path="/packages/:id" element={<PackageDetail />} />
                <Route path="/gatherings/:slug" element={<Gatherings />} />
                <Route path="/bookings/:id/track" element={<TrackInstructor />} />


                <Route path="/terms" element={<Terms />} />
                <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/instructor-terms" element={<InstructorTerms />} />
                <Route path="/partner-docs" element={<PartnerDocs />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/about" element={<About />} />
                <Route path="/dashboard" element={<Dashboard />}>
                  <Route index element={<Navigate to="bookings" replace />} />
                  <Route path="bookings" element={<DashboardBookings />} />
                  <Route path="my-vote" element={<DashboardMyVote />} />
                  <Route path="addresses" element={<DashboardAddresses />} />
                  <Route path="payments" element={<DashboardPayments />} />
                  <Route path="installments" element={<DashboardInstallments />} />
                  <Route path="gatherings" element={<DashboardGatherings />} />
                  <Route path="subscriptions" element={<DashboardSubscriptions />} />
                  <Route path="profile" element={<DashboardProfile />} />
                  
                  <Route path="settings" element={<DashboardSettings />} />
                  <Route path="notifications" element={<DashboardNotifications />} />
                  <Route path="passes" element={<DashboardPasses />} />
                  <Route path="passes/:id" element={<DashboardPassDetail />} />
                  <Route path="experiences" element={<DashboardExperiences />} />
                  <Route path="schools" element={<SchoolAdminDashboard />} />
                </Route>
                <Route path="/instructor" element={<RequireInstructor><InstructorDashboard /></RequireInstructor>}>
                  <Route path="bookings" element={<InstructorBookings />} />
                  
                  <Route path="revenue" element={<InstructorRevenue />} />
                  
                  <Route path="availability" element={<InstructorAvailability />} />
                  <Route path="calendar" element={<InstructorCalendar />} />
                  <Route path="profile" element={<InstructorProfile />} />
                </Route>
                <Route path="/instructor/activities/:id/edit" element={<RequireInstructor><EditActivity /></RequireInstructor>} />
                <Route path="/instructor/messages" element={<RequireInstructor><InstructorMessages /></RequireInstructor>} />

                <Route path="/admin" element={<RequireRole roles={['admin','super_admin']}><AdminDashboard /></RequireRole>} />
                <Route path="/admin/categories" element={<RequireRole roles={['admin','super_admin']}><CategoryManagement /></RequireRole>} />
                <Route path="/admin/users" element={<RequireRole roles={['admin','super_admin']}><UserManagement /></RequireRole>} />
                <Route path="/admin/home-page" element={<RequireRole roles={['admin','super_admin']}><HomePageManagement /></RequireRole>} />
                <Route path="/admin/schools" element={<RequireRole roles={['admin','super_admin']}><SchoolManagement /></RequireRole>} />
                <Route path="/admin/accounting" element={<RequireRole roles={['admin','super_admin']}><AccountingManagement /></RequireRole>} />
                <Route path="/admin/instructors" element={<RequireRole roles={['admin','super_admin']}><CompaniesManagement /></RequireRole>} />
                <Route path="/admin/messages" element={<RequireRole roles={['admin','super_admin','coo']}><AdminMessages /></RequireRole>} />
                <Route path="/admin/messages/:instructorId" element={<RequireRole roles={['admin','super_admin','coo']}><AdminMessages /></RequireRole>} />
                <Route path="/admin/b2b-companies" element={<RequireRole roles={['admin','super_admin']}><B2BCompaniesManagement /></RequireRole>} />
                <Route path="/admin/coupons" element={<RequireRole roles={['admin','super_admin']}><CouponManagement /></RequireRole>} />
                <Route path="/admin/applications" element={<RequireRole roles={['admin','super_admin']}><RoleApplications /></RequireRole>} />
                <Route path="/admin/passes" element={<RequireRole roles={['admin','super_admin']}><WalletPassesManagement /></RequireRole>} />
                <Route path="/admin/wallet-scanner" element={<RequireRole roles={['admin','super_admin','coo']}><WalletScanner /></RequireRole>} />
                <Route path="/admin/security" element={<RequireRole roles={['admin','super_admin']}><AdminSecurity /></RequireRole>} />
                <Route path="/admin/security-summary" element={<RequireRole roles={['admin','super_admin']}><SecuritySummary /></RequireRole>} />
                <Route path="/admin/seo-console" element={<RequireRole roles={['admin','super_admin']}><SeoConsole /></RequireRole>} />
                <Route path="/admin/site-settings" element={<RequireRole roles={['admin','super_admin']}><SiteSettings /></RequireRole>} />
                <Route path="/admin/subscription-discounts" element={<RequireRole roles={['admin','super_admin']}><SubscriptionDiscounts /></RequireRole>} />
                <Route path="/admin/gatherings" element={<RequireRole roles={['admin','super_admin']}><GatheringsManagement /></RequireRole>} />
                <Route path="/admin/shared-bank-accounts" element={<RequireRole roles={['admin','super_admin','cfo']}><SharedBankAccounts /></RequireRole>} />

                <Route path="/admin/audit-log" element={<RequireRole roles={['admin','super_admin']}><AuditLog /></RequireRole>} />
                <Route path="/admin/backup" element={<RequireRole roles={['admin','super_admin']}><BackupRestore /></RequireRole>} />
                <Route path="/admin/flash-deals" element={<RequireRole roles={['admin','super_admin']}><FlashDealsManagement /></RequireRole>} />
                <Route path="/admin/blog" element={<RequireRole roles={['admin','super_admin']}><BlogManagement /></RequireRole>} />
                <Route path="/admin/ab-testing" element={<RequireRole roles={['admin','super_admin']}><AbTestingManagement /></RequireRole>} />
                <Route path="/admin/webhooks" element={<RequireRole roles={['admin','super_admin']}><WebhooksManagement /></RequireRole>} />
                <Route path="/admin/packages" element={<RequireRole roles={['admin','super_admin','coo']}><PackagesManagement /></RequireRole>} />
                <Route path="/admin/upayments-log" element={<RequireRole roles={['admin','super_admin','cfo']}><UpaymentsLog /></RequireRole>} />
                <Route path="/cfo/upayments-log" element={<RequireRole roles={['admin','super_admin','cfo']}><UpaymentsLog /></RequireRole>} />
                <Route path="/b2b" element={<B2BPortal />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/ceo" element={<RequireRole roles={['super_admin']}><CEODashboard /></RequireRole>} />
                <Route path="/ceo/okrs" element={<RequireRole roles={['super_admin']}><CEOOkrs /></RequireRole>} />
                <Route path="/ceo/forecast" element={<RequireRole roles={['super_admin']}><CEOForecast /></RequireRole>} />
                <Route path="/ceo/health" element={<RequireRole roles={['super_admin']}><CEOHealth /></RequireRole>} />
                <Route path="/cfo" element={<RequireRole roles={['cfo','super_admin']}><CFODashboard /></RequireRole>} />
                <Route path="/cfo/accounting" element={<RequireRole roles={['cfo','super_admin']}><CFOAccounting /></RequireRole>} />
                <Route path="/cfo/payouts" element={<RequireRole roles={['cfo','super_admin']}><CFOPayouts /></RequireRole>} />
                <Route path="/cfo/refunds" element={<RequireRole roles={['cfo','super_admin']}><CFORefunds /></RequireRole>} />
                <Route path="/cfo/fraud" element={<RequireRole roles={['cfo','super_admin','admin','coo']}><CFOFraud /></RequireRole>} />
                <Route path="/cfo/vendor-earnings" element={<RequireRole roles={['cfo','super_admin']}><CFOVendorEarnings /></RequireRole>} />
                <Route path="/cfo/cashflow" element={<RequireRole roles={['cfo','super_admin']}><CFOCashflow /></RequireRole>} />
                <Route path="/cfo/revenue" element={<RequireRole roles={['cfo','super_admin']}><CFORevenueBreakdown /></RequireRole>} />
                <Route path="/cfo/pipeline" element={<RequireRole roles={['cfo','super_admin']}><CFOPipeline /></RequireRole>} />
                <Route path="/cfo/customer-value" element={<RequireRole roles={['cfo','super_admin']}><CFOCustomerValue /></RequireRole>} />
                <Route path="/cfo/bookings" element={<RequireRole roles={['cfo','super_admin']}><CFOMonthlyBookings /></RequireRole>} />
                <Route path="/cfo/settlements" element={<RequireRole roles={['cfo','super_admin']}><CFOSettlements /></RequireRole>} />
                
                <Route path="/cfo/costs" element={<RequireRole roles={['cfo','super_admin']}><CFOCosts /></RequireRole>} />
                <Route path="/cfo/budget" element={<RequireRole roles={['cfo','super_admin']}><CFOBudget /></RequireRole>} />

                <Route path="/coo" element={<RequireRole roles={['coo','super_admin']}><COODashboard /></RequireRole>} />
                <Route path="/coo/vendors" element={<RequireRole roles={['coo','super_admin']}><COOVendors /></RequireRole>} />
                <Route path="/coo/sla" element={<RequireRole roles={['coo','super_admin']}><COOSla /></RequireRole>} />
                <Route path="/coo/support" element={<RequireRole roles={['coo','super_admin']}><COOSupport /></RequireRole>} />
                <Route path="/pending-approval" element={<PendingApproval />} />
                <Route path="/schools" element={<SchoolsDirectory />} />
                <Route path="/schools/:id" element={<SchoolDetail />} />
                <Route path="/schools/:id/vote/:pollId" element={<SchoolVote />} />
                <Route path="/schools/:schoolId/winner/:pollId" element={<SchoolPollWinner />} />
                <Route path="/schools/create" element={<CreateSchool />} />
                <Route path="/schools/:schoolId/departments" element={<DepartmentSelection />} />
                <Route path="/schools/:schoolId/department/:subjectId" element={<DepartmentHeadPortal />} />
                <Route path="/schools/:schoolId/department/:subjectId/:stage" element={<DepartmentHeadPortal />} />
                <Route path="/polls/create" element={<CreateUserPoll />} />
                <Route path="/polls/:pollId" element={<UserPollVote />} />
                
                
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <FloatingWhatsApp />
            <FloatingCart />
          </BrowserRouter>
        </TooltipProvider>
        </CartProvider>
      </AuthProvider>
    </LocaleProvider>
  </QueryClientProvider>
  );
};

export default App;
